/**
 * Integrytas Group - Common Security Module (CSM)
 * Sign-in screen with remember-me, register link, and forgot-password link.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Info, ShieldCheck } from 'lucide-react';
import { APPLICATION_NAME, BRANDING_SUFFIX } from '../../constants/branding';
import { isRememberMe } from '../../lib/remember_me';
import { Watermark } from '../common/Watermark';
import { AboutModal } from '../common/AboutModal';
import { apiDb } from '../../lib/api_client';

export interface SignInScreenProps {
  onSignIn:           (email: string, password: string, rememberMe: boolean) => Promise<void>;
  onRequestRegister:  () => void;
  onRequestForgot:    () => void;
  onDevLogin?:        () => Promise<void>;
  errorMessage?:      string | null;
}

export function SignInScreen({
  onSignIn, onRequestRegister, onRequestForgot, onDevLogin, errorMessage,
}: SignInScreenProps) {
  const { t } = useTranslation();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy]         = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAbout, setShowAbout]       = useState(false);

  // DEV-ONLY quick login: in a dev build, submitting with BOTH email and password
  // blank signs in as the first super-admin (server-gated by
  // CSM.SECURITY.DEV_LOGIN_ENABLED). Never active in production (import.meta.env.DEV
  // is false there), and email-format validation stays intact for real logins.
  const devQuickLogin = import.meta.env.DEV
    && email.trim() === ''
    && password === '';

  // Background URL fetched from csm_system_parameters via the public RPC.
  // Mobile and desktop have separate values; we pick based on a media query.
  const [desktopBg, setDesktopBg] = useState<string>('');
  const [mobileBg,  setMobileBg]  = useState<string>('');
  const [isMobile,  setIsMobile]  = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );

  // Pre-tick the box if the user previously chose to be remembered.
  useEffect(() => { setRemember(isRememberMe()); }, []);

  // Track viewport size so we can swap mobile/desktop backgrounds live.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Load LOGIN_BACKGROUND / MOBILE_LOGIN_BACKGROUND once on mount.
  // The public RPC is anon-callable so this works on the unauthenticated screen.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await apiDb.rpc('csm_get_login_backgrounds');
      if (cancelled || error) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return;
      setDesktopBg(row.login_background ?? '');
      setMobileBg(row.mobile_login_background ?? '');
    })();
    return () => { cancelled = true; };
  }, []);

  // Effective URL: mobile param when on mobile and set, else desktop.
  // Mobile falls back to desktop when MOBILE_LOGIN_BACKGROUND is empty.
  const bgUrl = isMobile ? (mobileBg || desktopBg) : desktopBg;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (devQuickLogin && onDevLogin) {
        await onDevLogin();
      } else {
        await onSignIn(email, password, remember);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="relative flex h-screen w-screen items-center justify-center bg-csm-surface-alt"
      style={bgUrl
        ? {
            backgroundImage: `url("${bgUrl}")`,
            backgroundSize:     'cover',
            backgroundPosition: 'center',
            backgroundRepeat:   'no-repeat',
          }
        : undefined}
    >
      {/* Subtle scrim so the form stays legible over busy photography. */}
      {bgUrl && <div aria-hidden className="absolute inset-0 bg-black/25" />}

      <Watermark appName={APPLICATION_NAME} brandingSuffix={BRANDING_SUFFIX} />

      <form
        onSubmit={submit}
        className={
          'relative z-10 w-full max-w-sm border border-csm-border bg-white ' +
          (isMobile ? 'rounded-2xl p-7 shadow-lg' : 'rounded p-6 shadow-sm')
        }
      >
        <button
          type="button"
          onClick={() => setShowAbout(true)}
          aria-label={t('about.open_label', 'About')}
          title={t('about.open_label', 'About')}
          className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded text-csm-muted hover:bg-csm-surface-alt hover:text-csm-ink"
        >
          <Info className="h-4 w-4" />
        </button>

        {isMobile && (
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <ShieldCheck className="h-6 w-6" />
          </div>
        )}
        <h1 className={isMobile ? 'mb-1 text-xl font-bold text-csm-ink' : 'mb-1 text-csm-lg font-semibold text-csm-ink'}>
          {APPLICATION_NAME}
        </h1>
        <p className="mb-4 text-csm-sm text-csm-muted">{t('auth.sign_in')}.</p>

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.email')}</label>
        <input
          // type=email keeps format validation for real logins; required is dropped
          // only when both fields are blank (the dev shortcut) so it can submit.
          type="email" required={!devQuickLogin} autoComplete="username"
          value={email} onChange={e => setEmail(e.target.value)}
          className={
            'mb-3 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none ' +
            (isMobile ? 'h-11' : 'h-8')
          }
        />

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.password')}</label>
        <div className="relative mb-3">
          <input
            type={showPassword ? 'text' : 'password'}
            required={!devQuickLogin} autoComplete="current-password"
            value={password} onChange={e => setPassword(e.target.value)}
            className={
              'w-full rounded border border-csm-border pl-2 pr-9 text-csm-md focus:border-csm-watermark focus:outline-none ' +
              (isMobile ? 'h-11' : 'h-8')
            }
          />
          <button
            type="button"
            onClick={() => setShowPassword(s => !s)}
            aria-label={showPassword ? t('auth.hide_password', 'Hide password') : t('auth.show_password', 'Show password')}
            title={showPassword ? t('auth.hide_password', 'Hide password') : t('auth.show_password', 'Show password')}
            className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-csm-muted hover:text-csm-ink"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {devQuickLogin && (
          <p className="mb-3 -mt-1 text-csm-xs text-csm-muted">
            {t('auth.dev_quick_login_hint', 'Dev: sign in with both fields blank to log in as the first super-admin.')}
          </p>
        )}

        <div className="mb-3 flex items-center justify-between text-csm-sm">
          <label className="flex items-center gap-2 text-csm-ink-soft">
            <input
              type="checkbox"
              checked={remember}
              onChange={e => setRemember(e.target.checked)}
              className="h-3.5 w-3.5 accent-gray-700"
            />
            {t('auth.remember_me')}
          </label>
          <button
            type="button"
            onClick={onRequestForgot}
            className="text-csm-sm text-csm-ink-soft underline-offset-2 hover:text-csm-ink hover:underline"
          >
            {t('auth.forgot_password')}
          </button>
        </div>

        {errorMessage && (
          <div className="mb-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">
            {errorMessage}
          </div>
        )}

        <button
          type="submit" disabled={busy}
          className={
            'mb-3 w-full rounded border border-primary bg-primary text-csm-md font-medium text-white hover:bg-primary-hover disabled:opacity-60 ' +
            (isMobile ? 'h-11' : 'h-8')
          }
        >
          {busy ? t('auth.signing_in') : t('auth.sign_in')}
        </button>

        <div className="text-center text-csm-sm text-csm-ink-soft">
          {t('auth.new_to_app', { app: APPLICATION_NAME })}{' '}
          <button
            type="button"
            onClick={onRequestRegister}
            className="font-medium text-csm-ink underline-offset-2 hover:underline"
          >
            {t('auth.create_account')}
          </button>
        </div>
      </form>

      <AboutModal
        open={showAbout}
        onClose={() => setShowAbout(false)}
        appName={APPLICATION_NAME}
        isAuthenticated={false}
      />
    </div>
  );
}
